import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import TVStationOverlayShell from './TVStationOverlayShell';
import TVStationHeatmapCard from './TVStationHeatmapCard';
import { getTvSpotPanelTitles } from '../../data/tvDemoDisplay';
import { buildMultiDayHeatmap, buildWeekUnifiedHeatmap } from '../../utils/tvStationHeatmap';
import tvBroadcast from './tvBroadcastStyles';

function TVStationWeekPanel({ data, compact = false, loading = false, variant = 'future' }) {
  const { imageSrc, pastDays, today, tomorrow, dayAfter, laterDays, summary, locationId } = data;
  const isPast = variant === 'past' || Boolean(pastDays?.length);
  const fallbackTitles = getTvSpotPanelTitles(locationId || 'station');
  const panelTitle = data.panelTitle
    || (isPast ? fallbackTitles.pastWeek : fallbackTitles.week);

  const weekHeatmap = useMemo(() => {
    if (isPast) {
      return buildMultiDayHeatmap(pastDays ?? []);
    }
    return buildWeekUnifiedHeatmap({ today, tomorrow, dayAfter, laterDays });
  }, [isPast, pastDays, today, tomorrow, dayAfter, laterDays]);

  return (
    <Box sx={{ ...tvBroadcast.root, opacity: loading ? 0.5 : 1 }}>
      <TVStationOverlayShell
        imageSrc={imageSrc}
        title={panelTitle}
        caption={summary}
        compact={compact}
        airy
        contentMaxWidth="min(94%, 980px)"
      >
        <TVStationHeatmapCard
          rowHeader={weekHeatmap.rowHeader}
          rowLabels={weekHeatmap.rowLabels}
          colLabels={weekHeatmap.colLabels}
          cells={weekHeatmap.cells}
          compact={compact}
          sizePreset="stationOverlayWeek"
          airy
          fillWidth
        />
      </TVStationOverlayShell>
    </Box>
  );
}

TVStationWeekPanel.propTypes = {
  data: PropTypes.shape({
    locationName: PropTypes.string.isRequired,
    imageSrc: PropTypes.string,
    panelTitle: PropTypes.string,
    pastDays: PropTypes.arrayOf(PropTypes.object),
    today: PropTypes.object,
    tomorrow: PropTypes.object,
    dayAfter: PropTypes.object,
    laterDays: PropTypes.arrayOf(PropTypes.object),
    summary: PropTypes.string.isRequired,
  }).isRequired,
  compact: PropTypes.bool,
  loading: PropTypes.bool,
  variant: PropTypes.oneOf(['future', 'past']),
};

export default TVStationWeekPanel;
